import * as Sentry from "@sentry/react";
import { DocumentData, DocumentReference, setDoc } from "firebase/firestore";
import { DniImageType, FirebaseDniData } from "../../types";
import { maxFileSize, validDniTypes } from "../../utils";
import { heicToJpeg } from "../../app/utils";
import { InputFile } from "@almafintech/react-components/InputFile";
import SuccessCard from "../SuccessCard/SuccessCard";
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import { genericToastifyError } from "../../utils";

import styles from "./UploadDniCard.module.scss";

const dniFields: { name: DniImageType; label: string }[] = [
  {
    name: "frontDNI",
    label: "Frente",
  },
  {
    name: "backDNI",
    label: "Dorso",
  },
];

interface UploadDniFormProps {
  dniData: FirebaseDniData;
  dniRef: DocumentReference<DocumentData>;
  onUploadDni: (
    type: DniImageType,
    file: File,
    uuid: string,
    cuit: string
  ) => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingButton: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  isExternalLink: boolean;
  success: boolean;
  uuid: string;
  cuit: string;
}

const UploadDniForm = ({
  setError,
  setIsLoadingButton,
  onUploadDni,
  dniData,
  dniRef,
  isExternalLink,
  isMobile,
  success,
  uuid,
  cuit,
}: UploadDniFormProps) => {
  const { container, externalLink, mobileInput } = styles;

  const handleOnFileUpload = async (file: File, name: DniImageType) => {
    setError(false);
    setIsLoadingButton(true);

    try {
      if (!uuid || !cuit) throw new Error("UUID or CUIT is missing");

      const convertedFile = await heicToJpeg(file);
      await onUploadDni(name, convertedFile, uuid, cuit);
      saveFileToFirestore(name, convertedFile.name);
    } catch (error) {
      Sentry.captureException(error);
      setError(true);
      showToastMessage(genericToastifyError, {
        containerId: "formAlert",
        type: "error",
      });
    } finally {
      setIsLoadingButton(false);
    }
  };

  const saveFileToFirestore = (dniImage: DniImageType, fileName: string) =>
    setDoc(dniRef, { [dniImage]: fileName }, { merge: true });

  return (
    <form className={`${container} ${isExternalLink ? externalLink : ""} `}>
      {success ? (
        <SuccessCard
          title="¡Imágenes cargadas con éxito!"
          message="Podés cerrar esta página"
        />
      ) : (
        <>
          {dniFields.map(({ name, label }, index) => (
            <InputFile
              key={name}
              name={name}
              maxSize={maxFileSize}
              validTypes={validDniTypes}
              isMobile={isMobile}
              text={
                <p>
                  Arrastrá o <span>seleccioná un archivo</span> de tu
                  dispositivo
                </p>
              }
              className={isMobile ? mobileInput : ""}
              label={label}
              infoText={`Cargá un archivo JPG, PNG, HEIC, HEIF o PDF de hasta ${maxFileSize}mb.`}
              infoTextPosition="top"
              hideDownloadIcon
              fileData={
                dniData[name as DniImageType] !== ""
                  ? { id: index, name: dniData[name as DniImageType] }
                  : undefined
              }
              onFileUpload={(file) =>
                handleOnFileUpload(file, name as DniImageType)
              }
              onFileRemove={() => {
                saveFileToFirestore(name as DniImageType, "");
              }}
            />
          ))}
        </>
      )}
    </form>
  );
};

export default UploadDniForm;
